/**
 * Shared helpers for the multiple-bank-accounts-per-account contract.
 *
 * Reads, derivation, and payload building for both advisor and client APIs.
 * All helpers are pure and stateless; state lives in each screen's form data.
 *
 * Ops shape used by every caller:
 *   {
 *     addExisting: string[],                 // existing household bank IDs staged for link
 *     creates:     { tempId, ...fields }[],  // advisor-only inline creations
 *     updates:     { [baId]: partialFields },
 *     remove:      string[],                 // bank IDs staged for unlink
 *     unlinkAll?:  boolean,                  // client-only shorthand
 *   }
 *
 * Arrays + plain objects are used (instead of Set/Map) so ops survive React
 * form-state deep cloning and serialization without surprises.
 */

import { validateRoutingNumber } from "./validationHelpers";

export function canonicalBankAccountType(value) {
  if (value === null || value === undefined) return "";
  const trimmed = String(value).trim();
  if (!trimmed) return "";
  const lower = trimmed.toLowerCase();
  if (lower.includes("checking")) return "Checking";
  if (lower.includes("savings")) return "Savings";
  return trimmed;
}

export const BANK_TRACKED_KEYS = [
  "bank_name",
  "account_name",
  "account_number",
  "routing_number",
  "account_type",
  "link_type",
  "is_personal",
];

// ─── Reads ──────────────────────────────────────────────────────────────────

export function getAccountBanks(account) {
  if (!account) return [];
  const raw = account.bank_accounts;
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === "object") return [raw];
  return [];
}

export function getAccountBankIds(account) {
  if (!account) return [];
  const raw = account.bank_account_ids;
  if (Array.isArray(raw)) return raw.filter((id) => id != null);
  return getAccountBanks(account)
    .map((b) => b?.id)
    .filter((id) => id != null);
}

export function isBankLinkedToAccount(account, baId) {
  if (!baId) return false;
  return getAccountBankIds(account).includes(baId);
}

export function findHouseholdAccountsLinkedToBank(household, baId) {
  if (!household?.accounts || !baId) return [];
  return household.accounts.filter((a) => isBankLinkedToAccount(a, baId));
}

export function getUnlinkedHouseholdBanks(household, account) {
  const householdBanks = Array.isArray(household?.bank_accounts)
    ? household.bank_accounts
    : [];
  if (!householdBanks.length) return [];
  const linkedIds = new Set(getAccountBankIds(account));
  return householdBanks.filter((ba) => ba?.id && !linkedIds.has(ba.id));
}

export function formatBankLabel(ba) {
  if (!ba) return "Bank";
  const name = ba.bank_name || "Bank";
  const num = ba.account_number ? String(ba.account_number) : "";
  return `${name} - ${num || "N/A"}`;
}

// ─── Missing-field derivation ───────────────────────────────────────────────

function isValueMissing(key, value) {
  if (key === "is_personal") {
    return value === null || value === undefined;
  }
  return value === null || value === undefined || value === "";
}

export function isBankIncomplete(ba) {
  if (!ba || typeof ba !== "object") {
    return { incomplete: true, missingKeys: [...BANK_TRACKED_KEYS] };
  }
  const missingKeys = BANK_TRACKED_KEYS.filter((k) => isValueMissing(k, ba[k]));
  const routing = ba.routing_number;
  const hasRoutingValue =
    routing !== null && routing !== undefined && String(routing).trim() !== "";
  if (
    !missingKeys.includes("routing_number") &&
    hasRoutingValue &&
    !validateRoutingNumber(routing)
  ) {
    missingKeys.push("routing_number");
  }
  return { incomplete: missingKeys.length > 0, missingKeys };
}

export function deriveIncompleteBanks(account) {
  return getAccountBanks(account)
    .map((bank) => {
      const { incomplete, missingKeys } = isBankIncomplete(bank);
      return incomplete ? { bank, missingKeys } : null;
    })
    .filter(Boolean);
}

/**
 * Read the top-level `missing_fields.bank_accounts` map, keyed by bank account id:
 *   { "<bank_id>": { reasons: ["missing_data" | "missing_doc"] } }
 * Returns {} for any non-object / legacy shape.
 */
export function getMissingBankAccountsMap(missingFields) {
  const map = missingFields?.bank_accounts;
  return map && typeof map === "object" && !Array.isArray(map) ? map : {};
}

/**
 * Resolve the bank-account issues for a single account from the top-level
 * `missing_fields.bank_accounts` map. For each of the account's linked bank ids
 * present in the map, returns { bank, reasons, missingKeys }. The bank object is
 * hydrated from the account's embedded banks first, then `household.bank_accounts`
 * by id (accounts may carry only `bank_account_ids`), so missingKeys reflects the
 * real, freshest object. Backend is the source of truth for which banks appear.
 */
export function resolveAccountBankIssues(account, missingFields, household) {
  if (!account) return [];
  const map = getMissingBankAccountsMap(missingFields);
  const embedded = Object.fromEntries(
    getAccountBanks(account).filter((b) => b?.id).map((b) => [b.id, b]),
  );
  const hhById = Object.fromEntries(
    (household?.bank_accounts || []).filter((b) => b?.id).map((b) => [b.id, b]),
  );
  return getAccountBankIds(account)
    .filter((id) => map[id])
    .map((id) => {
      const bank = embedded[id] || hhById[id] || { id };
      const reasons = Array.isArray(map[id].reasons) ? map[id].reasons : [];
      return { bank, reasons, missingKeys: isBankIncomplete(bank).missingKeys };
    });
}

export function accountHasBankIssue(account, missingFields, household) {
  return resolveAccountBankIssues(account, missingFields, household).length > 0;
}

export function filterBankIssuesByReason(issues, reason) {
  return (issues || []).filter((i) => (i.reasons || []).includes(reason));
}

// ─── Ops-state helpers ──────────────────────────────────────────────────────

export function emptyBankOps() {
  return {
    addExisting: [],
    creates: [],
    updates: {},
    remove: [],
    unlinkAll: false,
  };
}

export function hasUnsavedBankChanges(ops) {
  if (!ops) return false;
  if (ops.unlinkAll) return true;
  if ((ops.addExisting || []).length > 0) return true;
  if ((ops.creates || []).length > 0) return true;
  if ((ops.remove || []).length > 0) return true;
  const updates = ops.updates || {};
  return Object.keys(updates).some((k) => updates[k] && Object.keys(updates[k]).length > 0);
}

/**
 * Compute the ordered list of banks the accordion should render for an account,
 * merging persisted banks with staged ops.
 */
export function resolveVisibleBanks(account, householdBanks, ops) {
  const removeSet = new Set(ops?.remove || []);
  const persisted = getAccountBanks(account).filter((b) => !removeSet.has(b?.id));

  const persistedIds = new Set(persisted.map((b) => b?.id).filter(Boolean));
  const hhById = Object.fromEntries(
    (householdBanks || []).filter((b) => b?.id).map((b) => [b.id, b])
  );

  const added = (ops?.addExisting || [])
    .filter((id) => !persistedIds.has(id))
    .map((id) => hhById[id])
    .filter(Boolean);

  const creates = (ops?.creates || []).map((c) => ({ ...c, id: c.id || c.tempId, _isNew: true }));

  const withUpdates = (list) =>
    list.map((b) => {
      const id = b.id || b.tempId;
      const patch = ops?.updates?.[id];
      return patch && Object.keys(patch).length > 0 ? { ...b, ...patch } : b;
    });

  return [...withUpdates(persisted), ...withUpdates(added), ...creates];
}

// ─── Payload builders — client ──────────────────────────────────────────────

/**
 * Build the client-side `bank_accounts: { link?, unlink?, update? }` op.
 * Returns `null` when there's nothing to emit so callers can omit the key.
 */
export function buildClientBankAccountsOp(ops) {
  if (!ops) return null;
  const op = {};

  const link = (ops.addExisting || []).filter(Boolean);
  if (link.length > 0) op.link = link;

  if (ops.unlinkAll) {
    op.unlink = true;
  } else {
    const unlink = (ops.remove || []).filter(Boolean);
    if (unlink.length > 0) op.unlink = unlink;
  }

  const updateDicts = Object.entries(ops.updates || {})
    .filter(([, partial]) => partial && Object.keys(partial).length > 0)
    .map(([id, partial]) => ({ id, ...partial }));
  if (updateDicts.length > 0) op.update = updateDicts;

  return Object.keys(op).length > 0 ? op : null;
}

// ─── Payload builders — advisor ─────────────────────────────────────────────

/**
 * Build the advisor-side `{ bank_accounts?, add_bank_accounts?, remove_bank_accounts? }`
 * block for `PUT /households/accounts/{id}` (or the bulk variant). Inline creates
 * and updates are merged into the single `bank_accounts` list, per the contract.
 * Returns `{}` when there's nothing to emit.
 */
export function buildAdvisorBankAccountsUpdate(ops) {
  const payload = {};
  if (!ops) return payload;

  const updates = Object.entries(ops.updates || {})
    .filter(([, partial]) => partial && Object.keys(partial).length > 0)
    .map(([id, partial]) => ({ id, ...partial }));

  const creates = (ops.creates || []).map((c) => {
    const { tempId, _isNew, id, ...fields } = c;
    return fields;
  });

  const combined = [...creates, ...updates];
  if (combined.length > 0) payload.bank_accounts = combined;

  const addExisting = (ops.addExisting || []).filter(Boolean);
  if (addExisting.length > 0) payload.add_bank_accounts = addExisting;

  const remove = (ops.remove || []).filter(Boolean);
  if (remove.length > 0) payload.remove_bank_accounts = remove;

  return payload;
}

/**
 * POST body helper for new account creation (advisor). Accepts an array of bank
 * dicts (ids optional) and returns `{ bank_accounts }`.
 */
export function buildAdvisorBankAccountsCreate(banks) {
  const list = (banks || [])
    .map((b) => {
      if (!b || typeof b !== "object") return null;
      const { tempId, _isNew, ...fields } = b;
      return fields;
    })
    .filter(Boolean);
  return list.length > 0 ? { bank_accounts: list } : {};
}

// ─── Form-state mutation helpers (small, shared by screens) ─────────────────

export function stageLinkExisting(ops, baId) {
  const base = ops || emptyBankOps();
  if (!baId) return base;
  if ((base.addExisting || []).includes(baId)) return base;
  const remove = (base.remove || []).filter((id) => id !== baId);
  return { ...base, remove, addExisting: [...(base.addExisting || []), baId] };
}

export function stageUnlink(ops, baId) {
  const base = ops || emptyBankOps();
  if (!baId) return base;
  if ((base.addExisting || []).includes(baId)) {
    return { ...base, addExisting: base.addExisting.filter((id) => id !== baId) };
  }
  if ((base.remove || []).includes(baId)) return base;
  const updates = { ...(base.updates || {}) };
  delete updates[baId];
  return { ...base, updates, remove: [...(base.remove || []), baId] };
}

export function stageCreate(ops, bankDict) {
  const base = ops || emptyBankOps();
  const tempId = bankDict?.tempId || `new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const entry = { ...bankDict, tempId };
  return { ...base, creates: [...(base.creates || []), entry] };
}

export function stageRemoveCreate(ops, tempId) {
  const base = ops || emptyBankOps();
  return { ...base, creates: (base.creates || []).filter((c) => c.tempId !== tempId) };
}

export function stageFieldEdit(ops, baId, field, value) {
  const base = ops || emptyBankOps();
  if (!baId) return base;
  const existingCreate = (base.creates || []).find((c) => c.tempId === baId || c.id === baId);
  if (existingCreate) {
    const creates = (base.creates || []).map((c) =>
      c.tempId === baId || c.id === baId ? { ...c, [field]: value } : c
    );
    return { ...base, creates };
  }
  const current = base.updates?.[baId] || {};
  const updates = { ...(base.updates || {}), [baId]: { ...current, [field]: value } };
  return { ...base, updates };
}
